import axios from "axios";
import IService from "./service";
import HttpErrorFactory from "../errors/http.factory";

const HEADER_AUTHORIZATION = 'Authorization';

class HttpRequest {
    /**
     * Constructor
     * @param client {axios}
     * @param config {Object}
     */
    constructor(client, config) {
        this.client = client;
        this.config = config;
        this.error = undefined;
        this.response = undefined;
    }

    /**
     * Set headers {header} value
     * @param header {string}
     * @param value {string}
     * @returns {HttpRequest}
     */
    withHeader(header, value) {
        const headers = {};
        headers[header] = value;
        return this.withHeaders(headers);
    }

    /**
     * Set multiple headers values
     * @param headers {Object}
     * @returns {HttpRequest}
     */
    withHeaders(headers) {
        this.config.headers = {...this.config.headers, ...headers};
        return this;
    }

    /**
     * Remove {header} from headers
     * @param header {string}
     * @returns {HttpRequest}
     */
    withoutHeader(header) {
        if (this.config.headers) {
            delete this.config.headers[header];
        }
        return this;
    }

    /**
     * Set Bearer token Authorization header
     * @param token {string}
     * @returns {HttpRequest}
     */
    withToken(token) {
        return this.withHeader(HEADER_AUTHORIZATION, `Bearer ${token}`);
    }

    /**
     * Removes Authorization header
     * @returns {HttpRequest}
     */
    withoutToken() {
        return this.withoutHeader(HEADER_AUTHORIZATION);
    }

    /**
     * Call http method and wrap error if raised
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async request(config) {
        try {
            this.response = await this.client.request({...this.config, ...config});
            return this.response;
        } catch (error) {
            this.error = HttpErrorFactory.axios(error);
            throw this.error;
        }
    }

    /**
     * Get shortcut
     * @param url {string}
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async get(url, config) {
        return await this.request({
            ...{
                method: 'get',
                url: url,
            },
            ...config
        });
    }

    /**
     * Post shortcut
     * @param url {string}
     * @param [data] {any}
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async post(url, data, config) {
        return await this.request({
            ...{
                method: 'post',
                url: url,
                data: data,
            },
            ...config
        });
    }

    /**
     * Put shortcut
     * @param url {string}
     * @param [data] {any}
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async put(url, data, config) {
        return await this.request({
            ...{
                method: 'put',
                url: url,
                data: data,
            },
            ...config
        });
    }

    /**
     * Patch shortcut
     * @param url {string}
     * @param [data] {any}
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async patch(url, data, config) {
        return await this.request({
            ...{
                method: 'patch',
                url: url,
                data: data,
            },
            ...config
        });
    }

    /**
     * Delete shortcut
     * @param url {string}
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async delete(url, config) {
        return await this.request({
            ...{
                method: 'delete',
                url: url,
            },
            ...config
        });
    }
}

export default class HttpService extends IService {
    /**
     * Constructor
     * @param provider {ServiceProvider}
     * @param config {Object}
     */
    constructor(provider, config) {
        super(provider, config);
        this.client = axios.create(this.config);
    }

    /**
     * Create new HttpRequest
     * @param [config] {Object}
     * @returns {HttpRequest}
     */
    create(config) {
        return new HttpRequest(
            this.client,
            {...this.config, ...config}
        );
    }

    /**
     * Shortcut for custom request
     * @param config {Object}
     * @returns {Promise<*>}
     */
    async request(config) {
        return this
            .create()
            .request(config);
    }

    /**
     * Shortcut for GET
     * @param url {string}
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async get(url, config) {
        return this
            .create()
            .get(url, config);
    }

    /**
     * Shortcut for POSY
     * @param url {string}
     * @param [data] {any}
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async post(url, data, config) {
        return this
            .create()
            .post(url, data, config);
    }

    /**
     * Shortcut for PUT
     * @param url {string}
     * @param [data] {any}
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async put(url, data, config) {
        return this
            .create()
            .put(url, data, config);
    }

    /**
     * Shortcut for PATCH
     * @param url {string}
     * @param [data] {any}
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async patch(url, data, config) {
        return this
            .create()
            .patch(url, data, config);
    }

    /**
     * Shortcut for DELETE
     * @param url {string}
     * @param [config] {Object}
     * @returns {Promise<*>}
     */
    async delete(url, config) {
        return this
            .create()
            .delete(url, config);
    }
}
